// filepath: apps/web/src/features/admin/userRoles/types.ts
//
// FR-UI-ADM-5 — TypeScript wire-type mirrors for the user-role
// assignment admin editor. Mirrors the Go wire types in
//   internal/auth/admin/user_role_admin_handler.go
// (KeycloakUser, listResponse, assignResponse, bulkAssignResponse,
//  syncRefreshResponse, versionConflictEnvelope).
//
// INV-5 contract (central): the SPA wire shape MUST NOT include a
// `password` / `client_secret` / `access_token` / `credentials`
// field on any endpoint response. The static guard in
// apps/web/src/runtime/staticGuards.test.ts scans this directory
// for any string field matching the FORBIDDEN_FIELD_NAME_RE regex
// and fails the build if one slips in (AC-12 invariant).

/**
 * FORBIDDEN_FIELD_NAME_RE — case-insensitive regex matching field
 * names that MUST NEVER appear in the FR-UI-ADM-5 wire shape.
 * Mirrors forbiddenFieldNameRe in internal/auth/admin/wire_contract.go
 * — the two MUST stay in sync.
 */
export const FORBIDDEN_FIELD_NAME_RE: RegExp =
  /(?:password|client_secret|client-secret|access_token|access-token|credentials)/i;

/**
 * SyncStatus — discriminated union of the 5 sync states surfaced
 * by the sync probe (FR-UI-ADM-5 AC-7). Mirrors SyncStatus in
 * internal/auth/admin/user_role_admin_handler.go.
 */
export type SyncStatus = "ok" | "degraded" | "failed";

/**
 * KeycloakUser — single row from GET /api/admin/users. Mirrors
 * KeycloakUser in internal/auth/admin/interfaces.go.
 *
 * INV-5: NO `password` / `credentials` / `client_secret` /
 * `access_token` field. The static guard regex enforces this.
 */
export type KeycloakUser = {
  id: string;
  username: string;
  display_name: string;
  email: string;
  assigned_roles: string[];
};

/**
 * UserRoleListResponse — GET /api/admin/users response shape.
 */
export type UserRoleListResponse = {
  users: KeycloakUser[];
  last_sync_at: string;
  sync_status: SyncStatus;
  version: number;
  sync_pending: boolean;
};

/**
 * AssignUserRolesRequest — PUT /api/admin/users/{user_id}/roles
 * body. `expected_sync_state_version` is the optimistic-concurrency
 * token echoed from the most recent GET; the server enforces it
 * via the `If-Match` header (INV-7).
 */
export type AssignUserRolesRequest = {
  roles: string[];
  expected_sync_state_version: number;
};

/**
 * RemoveUserRolesRequest — DELETE /api/admin/users/{user_id}/roles
 * body. Removes ALL assigned roles (matches the FR-UI-ADM-5 AC-8
 * orphan-cleanup semantics).
 */
export type RemoveUserRolesRequest = {
  expected_sync_state_version: number;
};

/**
 * BulkAssignRequest — POST /api/admin/users/roles/bulk-assign body.
 * Requires X-Idempotency-Key header (ARCH Rule 3).
 */
export type BulkAssignRequest = {
  user_ids: string[];
  roles: string[];
  expected_sync_state_version: number;
};

/**
 * BulkAssignResponse — partial-success envelope. Per-user Keycloak
 * calls inside the bulk handler carry an internal sha256 idempotency
 * key (defense-in-depth); the SPA does NOT auto-retry.
 */
export type BulkAssignResponse = {
  assigned: number;
  failed: Record<string, string>;
};

/**
 * SyncRefreshResponse — POST /api/admin/users/sync-refresh response.
 */
export type SyncRefreshResponse = {
  last_sync_at: string;
  sync_status: SyncStatus;
  version: number;
  orphans: string[];
};

/**
 * UserRoleAssignResponse — PUT /api/admin/users/{user_id}/roles 200
 * body. Returns the new merged roleset + the bumped version.
 */
export type UserRoleAssignResponse = {
  user_id: string;
  roles: string[];
  version: number;
};

/**
 * ErrorEnvelope — typed error envelope used by all error paths.
 * Mirrors the envelope shape in
 * internal/auth/admin/user_role_admin_handler.go.
 */
export type ErrorEnvelope = {
  error: string;
  code?: string;
  message?: string;
};

/**
 * VersionConflictError — typed envelope thrown by the
 * 409 USER_ROLE_VERSION_MISMATCH path. Mirrors
 * versionConflictEnvelope in
 * internal/auth/admin/user_role_admin_handler.go.
 */
export type VersionConflictError = ErrorEnvelope & {
  code: "USER_ROLE_VERSION_MISMATCH";
  current_version: number;
  server_user?: KeycloakUser;
};

/**
 * StagingOnlyError — typed envelope thrown by the 403 ENV_PRODUCTION
 * path (defense-in-depth — the SPA also gates the route).
 */
export type StagingOnlyError = ErrorEnvelope & {
  code: "staging_only";
};

/**
 * UserRoleFetchErrorKind — discriminated union mirroring
 * UserRoleFetchErrorKind in internal/auth/admin/errors.go. Maps
 * to the FR-UI-CORE-10 5-state fetch contract + the FR-UI-ADM-5
 * staging-only extension.
 */
export type UserRoleFetchErrorKind =
  | "loading"
  | "success"
  | "error_fetch"
  | "error_permission_denied"
  | "error_stale_version"
  | "error_staging_only";

/**
 * UserRoleFetchError — typed union used by the page component to
 * render the appropriate error surface (skeleton / error card /
 * conflict dialog).
 */
export type UserRoleFetchError = Error & {
  kind: UserRoleFetchErrorKind;
};

/**
 * classifyUserRoleFetchError — maps an HTTP error / thrown envelope
 * to the typed union. Mirrors classifyEndpointFetchError from
 * FR-UI-ADM-6.
 */
export function classifyUserRoleFetchError(
  status: number,
  body: ErrorEnvelope | null,
): UserRoleFetchError {
  const code = body?.code ?? "";
  const kind: UserRoleFetchErrorKind =
    status === 403 && code === "staging_only"
      ? "error_staging_only"
      : status === 403
        ? "error_permission_denied"
        : status === 409
          ? "error_stale_version"
          : "error_fetch";
  const err = new Error(
    body?.message ?? `http_${status}`,
  ) as UserRoleFetchError;
  err.kind = kind;
  (err as unknown as ErrorEnvelope).code = code;
  return err;
}
